/**
 * Pasted chat links of LINE OA Manager / Meta Business Suite (api/app/line_oa.py, api/app/meta_chat.py, routes in
 * api/app/platform_link.py, docs/api-contract.md "เปิดแชทนี้บนแพลตฟอร์ม"):
 *   GET    /api/v1/conversations/:id/platform-link         where the channel logo goes (+ `chat_link` for LINE /
 *                                                           Messenger / IG chats)
 *   PUT    /api/v1/conversations/:id/platform-chat {url}   "ผูกลิงก์แชทใน LINE OA / Meta Business Suite" (any staff, 1:1)
 *   DELETE /api/v1/conversations/:id/platform-chat         "เลิกผูก"
 *   GET/PUT /api/v1/config/line-oa {account_id, url}       the LINE OA's chat.line.biz link (owner writes, head reads)
 * Self-contained like ./fbhistory.ts (no change to the shared client): mock mode answers from localStorage
 * (`cf-chat.mock-lineoa`, so the screens scripts can preset it) and the mock db.
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { getApi, useMock } from './index'
import { MockChatApi } from './mock'
import { BAD_OA_URL, chatUrlProblem, parseChatUrl, parseOaUrl } from '../lib/lineOa'
import { parseMetaChatUrl } from '../lib/metaChat'

export interface ChatLinkState {
  /** LINE: the owner has set this OA's chat.line.biz link · Messenger / IG: always true (the Page's inbox) */
  configured: boolean
  /** staff pasted this customer's own chat link (the logo opens that chat) */
  linked: boolean
  /** "Cleanfithealthy (@447oxrpj)" / the Page's name — which OA / Page the links belong to */
  account: string
  linked_by: string | null
  linked_at: string | null
}

/** GET …/platform-link. `url` null = no page (LINE: the owner has not set the OA's link yet). */
export interface PlatformLinkState {
  url: string | null
  target: 'thread' | 'list' | 'inbox' | 'comment' | 'post' | null
  app: 'line' | 'meta' | 'facebook' | 'instagram' | null
  label: string | null
  note: string | null
  /** LINE / Messenger / IG 1:1 and LINE groups (null otherwise) */
  chat_link: ChatLinkState | null
}

export interface LineOaAccount {
  account_id: string
  name: string
  basic_id: string | null
  /** last 6 characters of the Messaging API bot user id (to tell two OAs apart, never used in a link) */
  bot_tail: string
  oa_id: string | null
  /** https://chat.line.biz/<oa id> once set */
  url: string | null
}

export interface LineOaSettings {
  items: LineOaAccount[]
  can_write: boolean
}

export interface PlatformChatApi {
  state(convId: string): Promise<PlatformLinkState>
  link(convId: string, url: string): Promise<PlatformLinkState>
  unlink(convId: string): Promise<PlatformLinkState>
  settings(): Promise<LineOaSettings>
  putSetting(accountId: string, url: string): Promise<LineOaSettings>
}

async function call<T>(method: 'GET' | 'PUT' | 'DELETE', path: string, body?: unknown, timeoutMs = 15000): Promise<T> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  const base = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
  try {
    const res = await fetch(new URL(`${base}/api/v1${path}`, window.location.origin).toString(), {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
    if (res.status === 401) reportUnauthorized()
    if (!res.ok) {
      let detail = res.statusText
      try {
        const j = (await res.json()) as { detail?: unknown }
        if (typeof j.detail === 'string') detail = j.detail
      } catch {
        /* body not JSON */
      }
      throw new ApiError(res.status, detail || `HTTP ${res.status}`)
    }
    return (await res.json()) as T
  } catch (e) {
    if (e instanceof ApiError) throw e
    if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
    throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
  } finally {
    clearTimeout(timer)
  }
}

const http: PlatformChatApi = {
  state: (id) => call('GET', `/conversations/${encodeURIComponent(id)}/platform-link`),
  link: (id, url) => call('PUT', `/conversations/${encodeURIComponent(id)}/platform-chat`, { url }),
  unlink: (id) => call('DELETE', `/conversations/${encodeURIComponent(id)}/platform-chat`),
  settings: () => call('GET', '/config/line-oa'),
  putSetting: (accountId, url) => call('PUT', '/config/line-oa', { account_id: accountId, url }),
}

// ------------------------------------------------------------------ mock (VITE_USE_MOCK, screens tests)

const MOCK_KEY = 'cf-chat.mock-lineoa'
const MOCK_ACCOUNT = { account_id: 'm1', name: 'Cleanfithealthy', basic_id: '@447oxrpj', bot_tail: 'a1b2c3' }
const MOCK_LABEL = `${MOCK_ACCOUNT.name} (${MOCK_ACCOUNT.basic_id})`
/** a synthetic Page id for the mock (never a real one) */
export const MOCK_PAGE = '100000000000001'
const MOCK_INBOX = `https://business.facebook.com/latest/inbox/all/?asset_id=${MOCK_PAGE}&mailbox_id=${MOCK_PAGE}`

interface MockStore {
  oa: string | null
  /** LINE: conversation id → OA Manager chat id */
  chats: Record<string, { chat: string; at: string }>
  /** Messenger / IG: conversation id → the kept Business Suite URL */
  meta?: Record<string, { url: string; item: string; at: string }>
}

function load(): MockStore {
  try {
    const v = JSON.parse(localStorage.getItem(MOCK_KEY) ?? 'null') as Partial<MockStore> | null
    return {
      oa: typeof v?.oa === 'string' ? v.oa : null,
      chats: v?.chats && typeof v.chats === 'object' ? v.chats : {},
      meta: v?.meta && typeof v.meta === 'object' ? v.meta : {},
    }
  } catch {
    return { oa: null, chats: {}, meta: {} }
  }
}

function save(s: MockStore): void {
  try {
    localStorage.setItem(MOCK_KEY, JSON.stringify(s))
  } catch {
    /* private window: the mock forgets on reload */
  }
}

function mockConv(convId: string): { channel: string; kind: string; history: boolean; account: string } {
  const c = (getApi() as MockChatApi).db.conversations.get(convId)
  if (!c) throw new ApiError(404, 'ไม่พบห้องแชทนี้')
  return { channel: c.channel, kind: c.kind, history: !!c.is_history, account: c.account?.name ?? '' }
}

const NONE: PlatformLinkState = { url: null, target: null, app: null, label: null, note: null, chat_link: null }

function mockState(convId: string): PlatformLinkState {
  const c = mockConv(convId)
  if (c.history) return NONE
  const s = load()
  if ((c.channel === 'fb' || c.channel === 'ig') && c.kind === 'dm') {
    const m = s.meta?.[convId] ?? null
    const chat_link: ChatLinkState = {
      configured: true,
      linked: !!m,
      account: c.account || (c.channel === 'fb' ? 'เพจ Facebook' : 'Instagram'),
      linked_by: m ? 'พลอย' : null,
      linked_at: m ? m.at : null,
    }
    return m
      ? { url: m.url, target: 'thread', app: 'meta', label: 'เปิดแชทนี้ใน Meta Business Suite', note: null, chat_link }
      : { url: MOCK_INBOX, target: 'inbox', app: 'meta', label: 'เปิดกล่องข้อความของเพจใน Meta Business Suite',
          note: 'ยังไม่ได้ผูกลิงก์แชทของลูกค้าคนนี้ ค้นชื่อลูกค้าในกล่องข้อความ', chat_link }
  }
  if (c.channel !== 'line' || (c.kind !== 'dm' && c.kind !== 'group')) return NONE
  const chat = c.kind === 'dm' && s.oa ? (s.chats[convId] ?? null) : null
  const chat_link: ChatLinkState = {
    configured: !!s.oa,
    linked: !!chat,
    account: MOCK_LABEL,
    linked_by: chat ? 'พลอย' : null,
    linked_at: chat ? chat.at : null,
  }
  if (!s.oa) return { url: null, target: null, app: 'line', label: null, note: null, chat_link }
  if (chat) {
    return { url: `https://chat.line.biz/${s.oa}/chat/${chat.chat}`, target: 'thread', app: 'line', label: 'เปิดแชทนี้ใน LINE OA', note: null, chat_link }
  }
  const note = c.kind === 'group' ? 'แชทกลุ่มเปิดตรงไม่ได้ หาแชทนี้ในรายการ' : 'ยังไม่ได้ผูกลิงก์แชทของลูกค้าคนนี้ ค้นชื่อลูกค้าในรายการแชท'
  return { url: `https://chat.line.biz/${s.oa}`, target: 'list', app: 'line', label: 'เปิดรายการแชทใน LINE OA', note, chat_link }
}

function mockSettings(): LineOaSettings {
  const oa = load().oa
  const role = (() => {
    try {
      return localStorage.getItem('cf-chat.mock-role')
    } catch {
      return null
    }
  })()
  return {
    items: [{ ...MOCK_ACCOUNT, oa_id: oa, url: oa ? `https://chat.line.biz/${oa}` : null }],
    can_write: role === 'owner',
  }
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

const mock: PlatformChatApi = {
  async state(convId) {
    await wait(80)
    return mockState(convId)
  },
  async link(convId, url) {
    await wait(150)
    const c = mockConv(convId)
    if (c.kind !== 'dm' || c.history || !['line', 'fb', 'ig'].includes(c.channel)) {
      throw new ApiError(409, 'ผูกลิงก์แชทได้เฉพาะแชทตัวต่อตัวของ LINE, Messenger และ Instagram')
    }
    const s = load()
    if (c.channel !== 'line') {
      const got = parseMetaChatUrl(url)
      if (typeof got === 'string') throw new ApiError(422, got)
      if (got.asset !== MOCK_PAGE) throw new ApiError(422, `ลิงก์นี้เป็นกล่องข้อความของเพจอื่น · เปิดแชทของลูกค้าคนนี้ใน ${c.account} แล้วคัดลอกลิงก์ใหม่`)
      const other = Object.entries(s.meta ?? {}).find(([id, v]) => v.item === got.item && id !== convId)
      if (other) throw new ApiError(409, 'ลิงก์แชทนี้ผูกกับลูกค้าอีกคนแล้ว · ตรวจว่าเปิดแชทของลูกค้าคนนี้อยู่ แล้วคัดลอกลิงก์ใหม่')
      const q = new URLSearchParams({ asset_id: got.asset })
      if (got.mailbox) q.set('mailbox_id', got.mailbox)
      q.set('selected_item_id', got.item)
      if (got.threadType) q.set('thread_type', got.threadType)
      s.meta = { ...(s.meta ?? {}), [convId]: { url: `https://business.facebook.com/latest/inbox/${got.path}/?${q}`, item: got.item, at: new Date().toISOString() } }
      save(s)
      return mockState(convId)
    }
    if (!s.oa) throw new ApiError(409, 'ยังไม่ได้ตั้งลิงก์หน้าแชท LINE OA ของบัญชีนี้ · เจ้าของตั้งได้ที่ ตั้งค่า › ช่องทางที่เชื่อม')
    const problem = chatUrlProblem(url, s.oa)
    if (problem) throw new ApiError(422, problem)
    const chat = parseChatUrl(url)![1]
    const other = Object.entries(s.chats).find(([id, v]) => v.chat === chat && id !== convId)
    if (other) throw new ApiError(409, 'ลิงก์แชทนี้ผูกกับลูกค้าอีกคนแล้ว · ตรวจว่าเปิดแชทของลูกค้าคนนี้อยู่ แล้วคัดลอกลิงก์ใหม่')
    s.chats[convId] = { chat, at: new Date().toISOString() }
    save(s)
    return mockState(convId)
  },
  async unlink(convId) {
    await wait(120)
    const s = load()
    delete s.chats[convId]
    if (s.meta) delete s.meta[convId]
    save(s)
    return mockState(convId)
  },
  async settings() {
    await wait(80)
    return mockSettings()
  },
  async putSetting(accountId, url) {
    await wait(150)
    if (accountId !== MOCK_ACCOUNT.account_id) throw new ApiError(404, 'ไม่พบ LINE OA นี้')
    const text = url.trim()
    const oa = text ? parseOaUrl(text) : null
    if (text && !oa) throw new ApiError(422, BAD_OA_URL)
    const s = load()
    s.oa = oa
    save(s)
    return { ...mockSettings(), can_write: true }
  },
}

/** Tests inject their own. */
let impl: PlatformChatApi | null = null
export function setPlatformChatApi(api: PlatformChatApi | null): void {
  impl = api
}

export function platformChatApi(): PlatformChatApi {
  if (impl) return impl
  return useMock && getApi() instanceof MockChatApi ? mock : http
}
